import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { RefreshCw, Send, Eye, Wallet } from 'lucide-react';
import { formatInr } from '../lib/formatInr';
import { GstSplitText } from './GstSplitText';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

const Stat = ({ label, count, total, tone, testId }) => (
  <div className={`border-2 border-black p-3 ${tone}`} data-testid={testId}>
    <div className="text-[10px] font-mono uppercase font-bold">{label}</div>
    <div className="text-xl font-black font-mono">{count}</div>
    <div className="text-xs font-mono">{formatInr(total)}</div>
  </div>
);

export const PendingPaymentsTab = ({ onOpenBooking }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState({});
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/admin/pending-payments`);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load pending payments');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remind = async (r) => {
    setMsg((p) => ({ ...p, [r.booking_id]: 'Sending…' }));
    try {
      await axios.post(`${API}/admin/bookings/${r.booking_id}/remind-top-up`);
      setMsg((p) => ({ ...p, [r.booking_id]: 'Reminder sent' }));
      load();
    } catch (e) {
      setMsg((p) => ({ ...p, [r.booking_id]: e?.response?.data?.detail || 'Send failed' }));
    }
  };

  const rows = (data?.rows || []).filter((r) => filter === 'all' || r.kind === filter);

  return (
    <div data-testid="pending-payments-tab" className="card-brutalist bg-white p-4 sm:p-6">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <h2 className="text-lg font-black font-mono uppercase flex items-center gap-2"><Wallet size={18} />Pending Payments</h2>
          <p className="text-xs font-mono text-gray-600 mt-1">Edited orders waiting for the customer to pay the difference, or for you to refund it.</p>
        </div>
        <button data-testid="pending-payments-refresh-btn" onClick={load} disabled={loading}
          className="px-3 py-2 border-2 border-black bg-white font-mono uppercase text-xs font-bold flex items-center gap-1 hover:bg-gray-100 disabled:opacity-50">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />Refresh
        </button>
      </div>

      {data && (
        <div className="grid grid-cols-2 gap-3 mb-4 max-w-md">
          <Stat label="Due from customers" count={data.top_up_count} total={data.top_up_total} tone="bg-[#FFF7E6]" testId="pending-topup-stat" />
          <Stat label="Refunds to process" count={data.refund_count} total={data.refund_total} tone="bg-green-50" testId="pending-refund-stat" />
        </div>
      )}

      <div className="flex gap-2 mb-3 flex-wrap">
        {[['all', 'All'], ['top_up', 'Payment due'], ['refund', 'Refund pending']].map(([k, l]) => (
          <button key={k} data-testid={`pending-filter-${k}`} onClick={() => setFilter(k)}
            className={`px-3 py-1.5 border-2 border-black font-mono uppercase text-[11px] font-bold ${filter === k ? 'bg-black text-white' : 'bg-white hover:bg-gray-100'}`}>
            {l}
          </button>
        ))}
      </div>

      {error && <p data-testid="pending-payments-error" className="text-red-600 text-sm font-mono mb-3">{error}</p>}

      <div className="overflow-x-auto border-2 border-black">
        <table className="w-full text-xs font-mono" data-testid="pending-payments-table">
          <thead className="bg-gray-100">
            <tr>
              {['Tag', 'Customer', 'Mobile', 'Type', 'Paid so far', 'New total', 'Difference', 'Edited', 'Pickup', 'Status', 'Actions'].map((h) => (
                <th key={h} className="text-left p-3 uppercase whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && !data ? (
              <tr><td colSpan={11} className="p-6 text-center text-gray-500">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={11} className="p-6 text-center text-gray-500" data-testid="pending-payments-empty">Nothing pending</td></tr>
            ) : rows.map((r) => (
              <tr key={r.booking_id} className="border-t border-gray-200 align-top" data-testid={`pending-row-${r.baggage_tag}`}>
                <td className="p-3 font-bold whitespace-nowrap">{r.baggage_tag}</td>
                <td className="p-3 min-w-[140px]">{r.customer_name || '—'}</td>
                <td className="p-3 whitespace-nowrap">{r.customer_phone || '—'}</td>
                <td className="p-3 whitespace-nowrap">
                  <span className={`px-2 py-0.5 border border-black font-bold ${r.kind === 'top_up' ? 'bg-[#FFE4CC] text-red-700' : 'bg-green-100 text-green-800'}`}>
                    {r.kind === 'top_up' ? 'PAYMENT DUE' : 'REFUND PENDING'}
                  </span>
                </td>
                <td className="p-3 whitespace-nowrap">{formatInr(r.paid_amount)}</td>
                <td className="p-3 whitespace-nowrap">{formatInr(r.new_amount)}</td>
                <td className={`p-3 whitespace-nowrap font-bold ${r.kind === 'top_up' ? 'text-red-700' : 'text-green-700'}`} data-testid={`pending-diff-${r.baggage_tag}`}>
                  {r.kind === 'top_up' ? '+' : '−'}{formatInr(r.difference)}
                  <div className="font-normal text-[10px] text-gray-600"><GstSplitText split={r.difference_split} /></div>
                </td>
                <td className="p-3 whitespace-nowrap">{fmt(r.edited_at)}</td>
                <td className="p-3 whitespace-nowrap">{r.scheduled_pickup_date || '—'}</td>
                <td className="p-3 whitespace-nowrap">{r.tracking_status}</td>
                <td className="p-3 min-w-[170px]">
                  <div className="flex gap-1 flex-wrap">
                    <button data-testid={`pending-open-${r.baggage_tag}`} onClick={() => onOpenBooking(r.booking_id)}
                      className="px-2 py-1 border-2 border-black bg-white uppercase font-bold flex items-center gap-1 hover:bg-gray-100">
                      <Eye size={11} />{r.kind === 'refund' ? 'Refund' : 'Open'}
                    </button>
                    {r.kind === 'top_up' && (
                      <button data-testid={`pending-remind-${r.baggage_tag}`} onClick={() => remind(r)}
                        className="px-2 py-1 border-2 border-black bg-[#FF4500] uppercase font-bold flex items-center gap-1">
                        <Send size={11} />Remind
                      </button>
                    )}
                  </div>
                  {r.kind === 'top_up' && r.reminder_count > 0 && (
                    <div className="text-[10px] text-gray-600 mt-1">Reminded {r.reminder_count}× · last {fmt(r.last_reminder_at)}</div>
                  )}
                  {msg[r.booking_id] && <div className="text-[10px] mt-1" data-testid={`pending-msg-${r.baggage_tag}`}>{msg[r.booking_id]}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
